import React from "react";
import PropTypes from "prop-types";
import i18n from "i18next";
import AtomicElementRenderer from "../../../../../plugins/AtomicBoxPlugin/components/AtomicElementRenderer";
import {
  getAtomicSampleData,
  SMART_ELEMENT_CONFIGS,
} from "../../../../../plugins/AtomicBoxPlugin/atomic_box_plugin_config";
import { ChartPreview, ShapePreview } from "./ElementPreview";

// Renders the drag-source slot content so the footprint matches what gets
// dropped: smart/text render the real component; shape/table/chart use faithful
// previews. (Image/video aren't here — they drag via data-url + data-media so
// paletteDrag shows the real placeholder.svg / video clip, like library files.)

const SMART_TYPES = new Set(SMART_ELEMENT_CONFIGS.map((c) => c.type));

// Plugins shown via faithful previews; the drag layer scales them to fit.
export const REAL_PLUGIN_KINDS = new Set(["ShapeOverlay", "GraficaD3"]);

export const hasRealPreview = (kind) =>
  kind === "BasicText" || SMART_TYPES.has(kind) || REAL_PLUGIN_KINDS.has(kind);

export const isScaleRealKind = (kind) => REAL_PLUGIN_KINDS.has(kind);

// On render error, fall back to empty so paletteDrag uses the card mockup.
class PreviewBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}
PreviewBoundary.propTypes = { children: PropTypes.node };

// No font-size here — it inherits from paletteDrag's container, which sets the
// canvas font (fontBase × slideScale) so the preview matches the real drop size.
const SmartReal = ({ type }) => {
  const state = {
    __text: encodeURIComponent(JSON.stringify({ elementData: getAtomicSampleData(type) })),
  };
  return (
    <div style={{ width: "100%" }}>
      <AtomicElementRenderer state={state} />
    </div>
  );
};
SmartReal.propTypes = { type: PropTypes.string };

const BasicTextReal = () => (
  <div style={{ lineHeight: 1.45, color: "#0f172a" }}>
    {i18n.t("text_here", { defaultValue: "Buraya istediğiniz yazıyı yazabilirsiniz." })}
  </div>
);

// Faithful ghosts reuse the exact card previews (content/intrinsic height).
const GHOSTS = {
  GraficaD3: () => <ChartPreview />,
  ShapeOverlay: () => <ShapePreview />,
};

const RealElementPreview = ({ kind }) => {
  if (kind === "BasicText") return <BasicTextReal />;
  if (SMART_TYPES.has(kind)) {
    return (
      <PreviewBoundary>
        <SmartReal type={kind} />
      </PreviewBoundary>
    );
  }
  if (GHOSTS[kind]) {
    const Ghost = GHOSTS[kind];
    return <Ghost />;
  }
  return null;
};

RealElementPreview.propTypes = {
  kind: PropTypes.string, // AtomicBox type or plugin name
};

export default RealElementPreview;
